import React, { useState, useEffect, useRef } from 'react'
import { 
  MessageSquare, 
  Send, 
  Play, 
  Pause, 
  Clock,
  AlertCircle,
  Bot,
  User
} from 'lucide-react'
import { watchlistAPI, promptAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const Prompt = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [response, setResponse] = useState('')
  const [autoRun, setAutoRun] = useState(false)
  const [autoRunInterval, setAutoRunInterval] = useState(null)
  const [watchlistSymbols, setWatchlistSymbols] = useState([])
  const [conversationHistory, setConversationHistory] = useState([])
  const intervalRef = useRef(null)

  useEffect(() => {
    if (user?.kiteConnected) {
      fetchWatchlistSymbols()
    }
  }, [user])

  useEffect(() => {
    if (autoRun && prompt.trim()) {
      startAutoRun()
    } else {
      stopAutoRun()
    }

    return () => stopAutoRun()
  }, [autoRun, prompt])

  const fetchWatchlistSymbols = async () => {
    try {
      const response = await watchlistAPI.getWatchlists()
      const allSymbols = []
      
      response.data.forEach(watchlist => {
        watchlist.instruments.forEach(instrument => {
          allSymbols.push(instrument.symbol)
        })
      })
      
      setWatchlistSymbols([...new Set(allSymbols)]) // Remove duplicates
    } catch (error) {
      console.error('Failed to fetch watchlist symbols:', error)
    }
  }

  const processPrompt = (userPrompt) => {
    let processedPrompt = userPrompt

    // Check if prompt mentions LTPs or stock data
    if (userPrompt.toLowerCase().includes('ltp') || 
        userPrompt.toLowerCase().includes('stock') ||
        userPrompt.toLowerCase().includes('price')) {
      
      if (watchlistSymbols.length > 0) {
        processedPrompt += `\n\nContext: User's watchlist contains these stocks: ${watchlistSymbols.join(', ')}`
      }
    }

    return processedPrompt
  }

  const sendPrompt = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt')
      return
    }

    setLoading(true)
    
    try {
      const processedPrompt = processPrompt(prompt)
      const response = await promptAPI.sendPrompt(processedPrompt)
      
      const newConversation = {
        id: Date.now(),
        prompt: prompt,
        response: response.data.response,
        timestamp: new Date()
      }
      
      setConversationHistory(prev => [newConversation, ...prev])
      setResponse(response.data.response)
      
      toast.success('Response received')
    } catch (error) {
      console.error('Prompt error:', error)
      toast.error('Failed to get response')
      
      // Mock response for development
      const mockResponse = generateMockResponse(prompt)
      const newConversation = {
        id: Date.now(),
        prompt: prompt,
        response: mockResponse,
        timestamp: new Date()
      }
      
      setConversationHistory(prev => [newConversation, ...prev])
      setResponse(mockResponse)
    } finally {
      setLoading(false)
    }
  }

  const generateMockResponse = (userPrompt) => {
    if (userPrompt.toLowerCase().includes('ltp') || userPrompt.toLowerCase().includes('price')) {
      if (watchlistSymbols.length > 0) {
        return `Based on your watchlist (${watchlistSymbols.join(', ')}), here's a mock analysis:

• Market sentiment appears bullish for tech stocks
• Consider monitoring volume patterns for ${watchlistSymbols[0] || 'your stocks'}
• Current market volatility suggests cautious approach
• Recommended: Set stop-losses at 5% below current levels

Note: This is a mock response. Connect ChatGPT API for real analysis.`
      }
    }
    
    return `Mock ChatGPT Response for: "${userPrompt}"

This is a simulated response. To get real ChatGPT responses:
1. Add your OpenAI API key in backend configuration
2. The system will automatically use your watchlist data for context
3. Auto-run feature will execute this prompt every 5 minutes when enabled

Your current watchlist: ${watchlistSymbols.length > 0 ? watchlistSymbols.join(', ') : 'No stocks in watchlist'}`
  }

  const startAutoRun = () => {
    if (intervalRef.current) return
    
    intervalRef.current = setInterval(() => {
      if (prompt.trim()) {
        sendPrompt()
      }
    }, 5 * 60 * 1000) // 5 minutes
    
    toast.success('Auto-run enabled (every 5 minutes)')
  }

  const stopAutoRun = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const toggleAutoRun = () => {
    setAutoRun(prev => {
      const newValue = !prev
      if (!newValue) {
        toast.info('Auto-run disabled')
      }
      return newValue
    })
  }

  if (!user?.kiteConnected) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kite Not Connected</h2>
        <p className="text-gray-600 mb-6">
          Please connect your Kite account in Settings to use AI prompts with your watchlist data.
        </p>
        <a href="/settings" className="btn-primary">
          Go to Settings
        </a>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">AI Prompt</h1>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              Watchlist: {watchlistSymbols.length} stocks
            </span>
          </div>
        </div>
      </div>

      {/* Prompt Input */}
      <div className="card">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter your prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask about your stocks, market analysis, or trading strategies..."
              rows={4}
              className="input resize-none"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={sendPrompt}
                disabled={loading || !prompt.trim()}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>Send Prompt</span>
              </button>
              
              <button
                onClick={toggleAutoRun}
                disabled={!prompt.trim()}
                className={`btn flex items-center space-x-2 ${
                  autoRun ? 'btn-danger' : 'btn-secondary'
                }`}
              >
                {autoRun ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{autoRun ? 'Stop Auto Run' : 'Auto Run (5min)'}</span>
              </button>
            </div>
            
            {autoRun && (
              <div className="flex items-center space-x-2 text-sm text-primary-600">
                <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse" />
                <span>Auto-run active</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Response */}
      {response && (
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Bot className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Latest Response</h3>
          </div>
          
          {/* Request */}
          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">Your Request:</span>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-gray-700">{prompt}</p>
            </div>
          </div>
          
          {/* Response */}
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Bot className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-600">AI Response:</span>
            </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
              {response}
            </pre>
            </div>
          </div>
        </div>
      )}

      {/* Conversation History */}
      {conversationHistory.length > 0 && (
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Conversation History</h3>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {conversationHistory.map((conversation) => (
              <div key={conversation.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3 mb-3">
                  <User className="w-4 h-4 text-gray-500 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">You</p>
                    <p className="text-sm text-gray-700 mt-1">{conversation.prompt}</p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {conversation.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Bot className="w-4 h-4 text-primary-600 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-600">AI Assistant</p>
                    <div className="bg-gray-50 rounded p-3 mt-1">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                        {conversation.response}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">How to Use</h3>
        </div>
        
        <div className="space-y-2 text-sm text-gray-600">
          <p>• Ask questions about your watchlist stocks (e.g., "analyze my LTPs")</p>
          <p>• Request market analysis or trading strategies</p>
          <p>• Use Auto Run to get periodic updates every 5 minutes</p>
          <p>• The AI will automatically include your watchlist context when relevant</p>
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Powered by Perplexity AI Sonar PRO for real-time financial analysis and market insights.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Prompt